import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { ProductSizeOption, Recipe, RecipeStand } from "../models/Recipe";

export const getRecipes = async (): Promise<Recipe[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "ActiveOnly", value: false }];

	const resp = await apiService.callV2("[MILL].[GetRecipes]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as Recipe[];
	}
	throw new Error(resp.message || "Error fetching recipes");
};

export const getRecipeStands = async (
	recipeId: number,
): Promise<RecipeStand[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "RecipeId", value: recipeId }];

	const resp = await apiService.callV2("[MILL].[GetRecipeStands]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RecipeStand[];
	}
	throw new Error(resp.message || "Error fetching recipe stands");
};

export const upsertRecipe = async (recipe: Recipe): Promise<number> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: recipe.Id },
		{ name: "Name", value: recipe.Name || "-" },
		{ name: "ProductSizeId", value: recipe.ProductSizeId ?? -1 },
		{ name: "LineSpeed", value: recipe.LineSpeed ?? -1 },
		{ name: "StartTemperature", value: recipe.StartTemperature ?? -1 },
		{ name: "EndTemperature", value: recipe.EndTemperature ?? -1 },
		{ name: "Active", value: recipe.Active },
		{ name: "EnableComparison", value: recipe.EnableComparison },
	];

	const resp = await apiService.callV2("[MILL].[UpsertRecipe]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0].Id", null) as number;
	}
	throw new Error(resp.message || "Error saving recipe");
};

const buildStandXml = (stand: RecipeStand): string => {
	const rollGroove =
		stand.RollGroove != null ? ` rollGroove="${stand.RollGroove}"` : "";
	const looperHeight =
		stand.LooperHeight != null ? ` looperHeight="${stand.LooperHeight}"` : "";
	const tc = stand.IsTensionControlEnabled ? "1" : "0";
	const lc = stand.IsLooperControlEnabled ? "1" : "0";

	return `<XMLRoot><Row id="${stand.StandId}" selected="1" tensionControl="${tc}" looperControlSelected="${lc}"${looperHeight}${rollGroove} /></XMLRoot>`;
};

const buildRFactorXml = (stand: RecipeStand): string => {
	const target =
		stand.RFactorTarget != null
			? ` rfactorTarget="${stand.RFactorTarget}"`
			: "";
	const min =
		stand.RFactorMin != null ? ` rfactorMin="${stand.RFactorMin}"` : "";
	const max =
		stand.RFactorMax != null ? ` rfactorMax="${stand.RFactorMax}"` : "";

	return `<XMLRoot><Row standId="${stand.StandId}"${target}${min}${max} /></XMLRoot>`;
};

export const upsertRecipeStand = async (
	recipeId: number,
	stand: RecipeStand,
): Promise<number> => {
	const apiService = new MESApiService();

	const standsXml = buildStandXml(stand);
	const rfactorXml = buildRFactorXml(stand);

	const parameters: spParameter[] = [
		{ name: "Id", value: recipeId },
		{ name: "Name", value: "-" },
		{ name: "ProductSizeId", value: -1 },
		{ name: "LineSpeed", value: -1 },
		{ name: "StartTemperature", value: -1 },
		{ name: "EndTemperature", value: -1 },
		{ name: "Stands", value: standsXml },
		{ name: "StandRfactors", value: rfactorXml },
	];

	const resp = await apiService.callV2("[MILL].[UpsertRecipe]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0].Id", null) as number;
	}
	throw new Error(resp.message || "Error saving recipe stand");
};

export const removeRecipe = async (id: number): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Id", value: id }];

	const resp = await apiService.callV2("[MILL].[RemoveRecipe]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting recipe");
	}
};

export const createRecipeEmpty = async (
	productSizeId: number,
	name?: string,
): Promise<number> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "productSizeId", value: productSizeId },
		{ name: "name", value: name || null },
	];

	const resp = await apiService.callV2(
		"[MILL].[CreateRecipeEmpty]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0].Id", null) as number;
	}
	throw new Error(resp.message || "Error creating recipe");
};

export const duplicateRecipe = async (
	recipeId: number,
	name?: string,
): Promise<number> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "recipeId", value: recipeId },
		{ name: "name", value: name || null },
	];

	const resp = await apiService.callV2("[MILL].[DuplicateRecipe]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0].Id", null) as number;
	}
	throw new Error(resp.message || "Error duplicating recipe");
};

export const getProductSizesForRecipe = async (
	includeInactive = false,
): Promise<ProductSizeOption[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "OnlyActives", value: !includeInactive },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetProductSizes]",
		parameters,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as Array<{
			Id: number;
			Name: string;
			ProductTypeName: string;
		}>;
		return rows.map((r) => ({
			Id: r.Id,
			Name: r.Name,
			ProductTypeName: r.ProductTypeName,
		}));
	}
	throw new Error(resp.message || "Error fetching product sizes");
};
